/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { SubsidiaryLedgerFCPaymentInfo } from "./info";
import { SubsidiaryLedgerFCPaymentService } from "services/subsidiary-ledger-fc-payment";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  accountNumber: "",
  entityName: "",
  currency: "USD",
  openingBalance: {
    debitFC: 0,
    debitVND: 0,
    creditFC: 0,
    creditVND: 0,
  },
  details: [
    {
      bookingDate: "",
      documentNumber: "",
      documentDate: "",
      description: "",
      correspondingAccount: "",
      exchangeRate: 0,
      discountDeadline: "",
      debitIncurredFC: 0,
      debitIncurredVND: 0,
      creditIncurredFC: 0,
      creditIncurredVND: 0,
      debitBalanceFC: 0,
      debitBalanceVND: 0,
      creditBalanceFC: 0,
      creditBalanceVND: 0,
    }
  ],
  totalIncurred: {
    debitFC: 0,
    creditFC: 0,
    debitVND: 0,
    creditVND: 0,
  },
  closingBalance: {
    debitFC: 0,
    creditFC: 0,
    debitVND: 0,
    creditVND: 0,
  },
  totalPages: "1",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function SubsidiaryLedgerFCPaymentDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await SubsidiaryLedgerFCPaymentService.getById(id);
          const apiData = response.data || response;

          const formatDateVN = (iso) => {
            if (!iso) return "";
            if (typeof iso === "string" && iso.includes("/")) return iso;
            const d = new Date(iso);
            return isNaN(d) ? iso : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          };

          // Signatures UI sync
          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          // ADAPTER: Unpack Nested (DTO) -> Flat (UI)
          const op = apiData.openingBalance || {};
          const ti = apiData.totalIncurred || {};
          const cl = apiData.closingBalance || {};

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            accountNumber: apiData.accountNumber || "",
            entityName: apiData.entityName || "",
            currency: apiData.currencyType || "USD", // DTO: currencyType -> UI: currency
            openingBalance: {
              debitFC: op.debit?.foreign || 0,
              debitVND: op.debit?.vnd || 0,
              creditFC: op.credit?.foreign || 0,
              creditVND: op.credit?.vnd || 0,
            },
            details: (apiData.details || []).map(d => ({
              ...d,
              documentDate: formatDateVN(d.documentDate),
              exchangeRate: d.exchangeRate || 0,
              discountDeadline: d.discountPeriod || "", // DTO: discountPeriod -> UI: discountDeadline
              debitIncurredFC: d.debit?.foreign || 0,
              debitIncurredVND: d.debit?.vnd || 0,
              creditIncurredFC: d.credit?.foreign || 0,
              creditIncurredVND: d.credit?.vnd || 0,
              debitBalanceFC: d.balanceDebit?.foreign || 0,
              debitBalanceVND: d.balanceDebit?.vnd || 0,
              creditBalanceFC: d.balanceCredit?.foreign || 0,
              creditBalanceVND: d.balanceCredit?.vnd || 0,
            })),
            totalIncurred: {
              debitFC: ti.debit?.foreign || 0,
              creditFC: ti.credit?.foreign || 0,
              debitVND: ti.debit?.vnd || 0,
              creditVND: ti.credit?.vnd || 0,
            },
            closingBalance: {
              debitFC: cl.debit?.foreign || 0,
              creditFC: cl.credit?.foreign || 0,
              debitVND: cl.debit?.vnd || 0,
              creditVND: cl.credit?.vnd || 0,
            },
            totalPages: apiData.totalPages || "1",
            openingDate: formatDateVN(apiData.openingDate),
            reportDate: apiData.reportDate || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
          });
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/subsidiary-ledger-fc-payments");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);
      const pack = (fcProp, vndProp) => ({ foreign: num(fcProp), vnd: num(vndProp) });

      // ADAPTER: Pack Flat (UI) -> Nested (DTO)
      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        accountNumber: fValues.accountNumber,
        entityName: fValues.entityName,
        currencyType: fValues.currency, // UI: currency -> DTO: currencyType
        openingBalance: {
          debit: pack(fValues.openingBalance.debitFC, fValues.openingBalance.debitVND),
          credit: pack(fValues.openingBalance.creditFC, fValues.openingBalance.creditVND),
        },
        details: (fValues.details || []).map(d => ({
          bookingDate: d.bookingDate,
          documentNumber: d.documentNumber,
          documentDate: d.documentDate,
          description: d.description,
          correspondingAccount: d.correspondingAccount,
          exchangeRate: num(d.exchangeRate),
          discountPeriod: d.discountDeadline, // UI: discountDeadline -> DTO: discountPeriod
          debit: pack(d.debitIncurredFC, d.debitIncurredVND),
          credit: pack(d.creditIncurredFC, d.creditIncurredVND),
          balanceDebit: pack(d.debitBalanceFC, d.debitBalanceVND),
          balanceCredit: pack(d.creditBalanceFC, d.creditBalanceVND),
        })),
        totalIncurred: {
          debit: pack(fValues.totalIncurred.debitFC, fValues.totalIncurred.debitVND),
          credit: pack(fValues.totalIncurred.creditFC, fValues.totalIncurred.creditVND),
        },
        closingBalance: {
          debit: pack(fValues.closingBalance.debitFC, fValues.closingBalance.debitVND),
          credit: pack(fValues.closingBalance.creditFC, fValues.closingBalance.creditVND),
        },
        totalPages: String(fValues.totalPages),
        openingDate: fValues.openingDate,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
      };

      console.log("🚀 PAYLOAD SUBMIT TO DTO (Nested):", payload);

      if (!id) {
        await SubsidiaryLedgerFCPaymentService.create(payload);
        toast.success("Tạo sổ thành công!");
      } else {
        await SubsidiaryLedgerFCPaymentService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/subsidiary-ledger-fc-payments");
    } catch (e) {
      console.error("Save failed:", e);
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S32-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()],500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center"><Grid item xs={12}>
          <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
            {({ values, isSubmitting, setFieldValue }) => (
              <Form autoComplete="off"><Card><SoftBox p={0}>
                <SubsidiaryLedgerFCPaymentInfo formData={{ values, setFieldValue, containView }} />
                <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                  <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                  {containView ? (
                    <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Sổ S32-DN</SoftButton>
                  ) : (
                    <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                  )}
                </SoftBox>
              </SoftBox></Card></Form>
            )}
          </Formik>
        </Grid></Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default SubsidiaryLedgerFCPaymentDetail;
